import styles from "./SubscriptionInfo.module.css";
import Toggle from "../.shared/Toggle/Toggle";
import Pricing from "../../data/Pricing";
import { trackPostHogEvent } from "../../utils/posthogUtils";
import { SourceContext } from "../../utils/sourceContext";
import { useContext } from "react";
import { useFeatureFlagVariantKey } from 'posthog-js/react';


function getDisplayedPlanPrice(plan) {
    if (plan?.first_quarter_price_id && plan?.first_quarter_price) {
        return plan.first_quarter_price;
    }
    return plan?.total;
}

export default function SubscriptionInfo({ planId, onChangeFrequency, initialFrequency }) {

    // Remove Checkout Toggle Experiment
    const checkoutToggleExperimentVariant = useFeatureFlagVariantKey('remove-checkout-frequency-toggle-experiment');

    const { source } = useContext(SourceContext);

    const pricing = Pricing();
    const isWholeSale = pricing.isWholesale();
    const currentPlan = pricing.getPlanById(planId);
    const pricingOptions = pricing.getPricingOptionsByProductName(currentPlan?.product?.toLowerCase());
    const retailPricingOptions = pricing.getRetailPrices()[currentPlan?.product?.toLowerCase()];
    const frequency = currentPlan?.subscription_length === '1 Month' ? 'monthly' : 'threeMonths';
    const monthlyPlanHasThirdMonthFreeOffer = Boolean(pricingOptions?.[currentPlan?.formulation]?.monthly?.third_month_price_id);
    const quarterlyPlan = pricingOptions?.[currentPlan?.formulation]?.threeMonths;
    const quarterlySavingsLabel = quarterlyPlan?.first_quarter_price_id && quarterlyPlan?.first_quarter_price
        ? `Save $${pricing.calculateDiscount(quarterlyPlan?.total, quarterlyPlan?.first_quarter_price)} with promo`
        : `Save ${pricing.calculateQuarterlySavingsPercentage(pricingOptions?.[currentPlan?.formulation]?.monthly?.total, quarterlyPlan?.total)}%`;
    const displayedCurrentPlanPrice = getDisplayedPlanPrice(currentPlan);
    const isQuarterlyPlan = currentPlan?.subscription_length === '3 Months';
    const hasFirstQuarterDiscount = Boolean(isQuarterlyPlan && currentPlan?.first_quarter_price_id && currentPlan?.first_quarter_price);
    const topLinePrice = isWholeSale ? retailPricingOptions?.[currentPlan?.formulation]?.[frequency]?.total : displayedCurrentPlanPrice;
    const nextPaymentAmount = isQuarterlyPlan ? currentPlan?.total : displayedCurrentPlanPrice;
    const partnerDiscountAmount = isWholeSale ? pricing.calculateDiscount(retailPricingOptions?.[currentPlan?.formulation]?.[frequency]?.total, currentPlan.total, true) : null;
    const firstQuarterDiscountAmount = hasFirstQuarterDiscount
        ? pricing.calculateDiscount(currentPlan?.total, currentPlan?.first_quarter_price, true)
        : null;

    const nextDate = new Date();

    if (currentPlan?.name && currentPlan.name.includes('4')) {
        nextDate.setDate(nextDate.getDate() + 28);
    } else if (currentPlan?.name && currentPlan.name.includes('3')) {
        nextDate.setDate(nextDate.getDate() + 84);
    } else if (currentPlan?.name && currentPlan.name.includes('Monthly')) {
        nextDate.setDate(nextDate.getDate() + 28);
    }

    const nextPaymentDue = nextDate.toLocaleDateString();

    const handleChangeFrequency = (newFrequency) => {
        const newPlanId = pricingOptions?.[currentPlan?.formulation]?.[newFrequency]?.id;
        onChangeFrequency(newPlanId);
    }

    const handleClickUpsale = (newFrequency) => {

        if (newFrequency !== 'threeMonths') {
            handleChangeFrequency(newFrequency);
            return;
        }
    
        trackPostHogEvent('checkout_duration_upsale_clicked', {
            product: currentPlan?.product,
            formulation: currentPlan?.formulation,
            source,
            is_wholesale: pricing.isWholesale(),
            from_frequency: '1 Month',
            to_frequency: '3 Months',
            from_total: Math.round(Number.parseFloat(pricingOptions?.[currentPlan?.formulation]?.monthly?.total) * 100),
            to_total: Math.round(Number.parseFloat(pricingOptions?.[currentPlan?.formulation]?.threeMonths?.total) * 100),
        });

        handleChangeFrequency('threeMonths');
    }

    return (
        <div className={styles.subscriptionInfoContainer}>
            <div className={styles.subscriptionInfoHeader}>
                <h2 className={styles.subscriptionInfoTitle}>You are subscribing to</h2>
            </div>

            <div className={styles.subscriptionInfoBody}>
                <div className={styles.detailsRow}>
                    <span className={styles.detailLabel}>{currentPlan?.product} {currentPlan?.formulation === 'suspension' ? 'Oral drops' : 'Injectable'}</span>
                    <div className={styles.priceContainer}>
                        <div className={styles.detailValue}>
                            <span>${topLinePrice?.split('.')[0]}</span>
                            <span className={styles.cents}>.{topLinePrice?.split('.')[1]}</span>
                        </div>
                        <span className={styles.detailPer}>{currentPlan?.subscription_length === '1 Month' ? 'monthly' : 'quarterly'}</span>
                    </div>
                </div>
                {isWholeSale && (
                    <div className={styles.detailsRow}>
                        <span className={styles.detailLabel}>Partner discount</span>
                        <div className={styles.detailValue}>
                            <span>-${partnerDiscountAmount?.split('.')[0]}</span>
                            <span className={styles.cents}>.{partnerDiscountAmount?.split('.')[1]}</span>
                        </div>
                    </div>
                )}
                <div className={styles.detailsRow}>
                    <span className={styles.detailLabel}>Supplies</span>
                    <span className={styles.detailValue}>$0</span>
                </div>
                <div className={styles.detailsRow}>
                    <span className={styles.detailLabel}>Consultations</span>
                    <span className={styles.detailValue}>$0</span>
                </div>
                <div className={styles.detailsRow}>
                    <span className={styles.detailLabel}>Shipping</span>
                    <span className={styles.detailValue}>$0</span>
                </div>
                {hasFirstQuarterDiscount && (
                    <div className={styles.detailsRow}>
                        <span className={styles.detailLabel}>Promotional discount</span>
                        <div className={styles.detailValue}>
                            <span>-${firstQuarterDiscountAmount?.split('.')[0]}</span>
                            <span className={styles.cents}>.{firstQuarterDiscountAmount?.split('.')[1]}</span>
                        </div>
                    </div>
                )}

                <div className={styles.yourPriceContainer}>
                    <span className={styles.yourPriceLabel}>Your Price*</span>
                    <div className={styles.yourPriceValueContainer}>
                        <div className={styles.yourPriceValue}>
                            <span>${displayedCurrentPlanPrice?.split('.')[0]}</span>
                            <span className={styles.cents}>.{displayedCurrentPlanPrice?.split('.')[1]}</span>
                        </div>
                    </div>
                </div>
                
                {(initialFrequency === 'monthly' || checkoutToggleExperimentVariant === 'control') && (
                    <Toggle
                        options={[
                            {
                                label: <>
                                    Monthly
                                    {monthlyPlanHasThirdMonthFreeOffer && <><br /><span className={styles.save}>Third month free!</span></>}
                                </>,
                                value: "monthly"
                            },
                            { 
                                value: "threeMonths",
                                label: <>Quarterly<br /><span className={styles.save}>{quarterlySavingsLabel}</span></>
                            }
                        ]}
                        value={frequency}
                        onChange={handleClickUpsale}
                    />
                )}

                <span className={styles.frequencyNote}>
                    {currentPlan?.subscription_length === '1 Month' ? '4 week' : '12 week'} supply. Billed every {currentPlan?.subscription_length === '1 Month' ? '28 days' : '84 days'}. 
                    Cancel anytime. No hidden fees.
                </span>

                <div className={styles.additionalInfo}>
                    <span className={styles.infoText}>
                       * After today, your next payment of ${nextPaymentAmount} will be charged on {nextPaymentDue} and recur every {currentPlan?.subscription_length === '1 Month' ? '28 days' : '84 days'}.
                       {monthlyPlanHasThirdMonthFreeOffer && currentPlan?.subscription_length === '1 Month' && ' After your first two months, you will receive the third month FREE!'}
                    </span>
                </div>

            </div>
        </div>
    )
}